import axios from 'api/http-client';
import { appConfig } from 'config/app-config';
import { DefaultError } from 'errors/DefaultError';
import { ApiError } from 'errors/ApiError';
import { SessionError } from 'errors/SessionError';
import type { AxiosRequestConfig, Method } from 'axios';

const { API_URL } = appConfig;

type TClientParams = AxiosRequestConfig & {
  method?: Method;
  body?: object;
};

export async function client(
  endpoint: string,
  { method, body, ...customConfig }: TClientParams = {},
  apiUrl: string = API_URL
) {
  const headers = {
    ...customConfig.headers,
    'Content-Type': 'application/json; charset=UTF-8'
  };
  const requestConfig = {
    method,
    withCredentials: true,
    ...customConfig,
    headers
  };

  if (body) {
    requestConfig.data = JSON.stringify(body);
  }

  const url = `${apiUrl}/${endpoint}`;
  const response = await axios(url, requestConfig);

  return response.data;
}

export type TError = {
  response?: {
    config?: AxiosRequestConfig;
    data?: { errors?: Array<{ code?: string; [key: string]: unknown }> };
  };
  message?: string;
};

// eslint-disable-next-line complexity
export const getErrors = (error?: TError) => {
  if (axios.isCancel(error)) {
    return null;
  }
  if (error instanceof DefaultError) {
    return [error.toJson()];
  }
  const errorObj = JSON.parse(JSON.stringify(error));
  const responseErrors = error?.response?.data?.errors;
  const hasApiValidationErrors = !!responseErrors?.length;
  const isSessionError =
    responseErrors?.length === 1 &&
    responseErrors?.[0].code === 'ForbiddenException';

  if (isSessionError) {
    const sessionError = new SessionError({ error: error?.response });
    return [sessionError.toJson()];
  }

  if (hasApiValidationErrors) {
    const apiResponseError = new ApiError({
      error: error?.response,
      message: null,
      title: null
    });
    return apiResponseError.response?.errors;
  }

  try {
    const apiError = new ApiError({
      error: error?.response,
      message: `${error?.message} ${error?.response?.config?.url}`,
      title: null
    });

    return [apiError.toJson()];
  } catch {
    const ErrorDefault = new DefaultError();

    return [
      {
        ...ErrorDefault,
        message: errorObj.message || ErrorDefault.message
      }
    ];
  }
};
